import { DotsHorizontalIcon } from "@heroicons/react/outline";
import clsx from "clsx";
import parse from "html-react-parser";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import React, { ReactNode, useRef } from "react";

import { OutlinedButton } from "@/features/common/components/button/OutlinedButton";
import { PriceWithCurrency } from "@/features/common/components/price/PriceWithCurrency";
import { JoinSubscriptionButton } from "@/features/subscription/components/joinSubscriptionDialog/refactored/JoinSubscriptionButton";
import { useSubscriptionCardButtonStatus } from "@/features/subscription/hooks/useSubscriptionCardButtonStatus";
import styles from "@/features/subscriptionPlan/styles/subscriptionPlanCard.module.css";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import { SubscriptionPlanFullFragment } from "@/generated/generated-hooks";

import { SubscriptionPlanCardImage } from "./SubscriptionPlanCardImage";

const StandardPopoverButton = dynamic(
  () => import("@/features/common/components/layout/StandardPopoverButton"),
  { ssr: false },
);

type SubscriptionPlanCardProps = {
  subscriptionPlan: SubscriptionPlanFullFragment;
  hideButton?: boolean;
  actionButton?: ReactNode;
  isUnlockExpiredContent?: boolean;
  editMode?: boolean;
  onClickEdit?: () => void;
  // onClickRemove?: () => void;
  onClickSubscribe?: () => void;
  canUnlockPost?: boolean;
  suggestPlan?: boolean;
};

/**
 * @purpose Display a subsction plan in card form
 */
export const SubscriptionPlanCard: React.FC<SubscriptionPlanCardProps> = ({
  actionButton,
  subscriptionPlan,
  canUnlockPost = true,
  hideButton = false,
  editMode = false,
  onClickEdit,
  onClickSubscribe,
  isUnlockExpiredContent = false,
  suggestPlan = false,
}) => {
  const { user } = useUserContext();
  const { t } = useTranslation(["subscriptionPlan", "common"]);
  const descriptionRef = useRef<HTMLDivElement | null>(null);

  const {
    title,
    price,
    writerId,
    content,
    currency,
    channels,
    currentUserSubscriptionStatus,
  } = subscriptionPlan;

  // To check if user is subscribed already
  const {
    label,
    isDisabled: isDisabledFromServer,
    isInvalid,
  } = useSubscriptionCardButtonStatus(
    currentUserSubscriptionStatus,
    isUnlockExpiredContent,
  );

  const displayButton = !isInvalid && !hideButton;
  // const isDisabled = isDisabledFromServer;
  const isDisabled = isDisabledFromServer || !canUnlockPost;

  const getActionButton = () => {
    if (!displayButton) return null;
    if (!!actionButton) return actionButton;

    if (
      onClickSubscribe &&
      displayButton &&
      user?._id !== writerId &&
      !isDisabled
    ) {
      return (
        <JoinSubscriptionButton
          label={label}
          onClickSubscribe={onClickSubscribe}
          subscriptionPlan={subscriptionPlan}
        />
      );
    }

    if (user && isDisabled) {
      const getLabel = () => {
        //if the plan can unlock the post, but status is still disabled, we can return label derived from the status returned by server directly.
        if (canUnlockPost) return label;
        // if (isUnlockExpiredContent) return t("subscriptionPlan:sub");

        return t("subscriptionPlan:subscribe");
      };

      return (
        <OutlinedButton
          variant="secondary"
          fullWidth
          buttonProps={{ disabled: true }}
        >
          {getLabel()}
        </OutlinedButton>
      );
    }

    return null;
  };

  const editModeOptions = [
    {
      content: t("subscriptionPlan:menu.edit"),
      onClick: onClickEdit,
      as: "button" as const,
    },
  ];

  return (
    <div
      className={clsx(
        "relative flex flex-col max-w-[400px] w-full h-full rounded-xl overflow-hidden",
        suggestPlan ? "border-primary border-2" : "border-gray-300 border",
      )}
    >
      {/* Image */}
      <SubscriptionPlanCardImage subscriptionPlan={subscriptionPlan} />
      <div className="flex flex-col flex-1">
        <div className="flex flex-col px-2 py-4 bg-gray-200">
          {/* Title */}
          <span className="flex justify-center w-full text-xl text-center text-gray-900">
            {title}
          </span>

          {/* Price and Period */}
          <div className="flex items-center justify-center w-full mt-1 text-gray-900 whitespace-pre">
            <PriceWithCurrency
              currency={currency}
              price={price}
              className="text-[1.3rem] font-bold"
            />

            {!isUnlockExpiredContent && (
              <span className="text-base  ">{` / ${t(
                "subscriptionPlan:period.perMonth",
              )}`}</span>
            )}
          </div>
        </div>

        <div className="flex flex-col justify-between flex-1 px-3 pt-6 pb-5 space-y-4 rounded-b-xl">
          <div className="">
            {/* Description */}
            <div
              ref={descriptionRef}
              className={clsx(
                "h-16 overflow-y-auto scrollbar-thin scrollbar-thumb-[#bdbdbd] scrollbar-track-[#f5f5f5] scrollbar-thumb-rounded-full text-center",
                styles.richTextContainer,
              )}
            >
              {parse(content.serialized ?? "")}
            </div>
          </div>

          <div className="flex justify-center ">{getActionButton()}</div>
        </div>
      </div>
      {editMode && (
        <div className="absolute z-10 top-2 right-2 rounded-full bg-black/20">
          <StandardPopoverButton
            icon={<DotsHorizontalIcon className="w-5 h-5 text-white" />}
            options={editModeOptions}
          />
        </div>
      )}
    </div>
  );
};
